import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight, Check, CheckCircle, XCircle, Phone } from 'lucide-react';
import axios from 'axios';
import { submitLead } from '../../services/hubspot';
import PrivacyCheckbox from '../../components/PrivacyCheckbox';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL;

const steps = [
  { num: '01', title: 'Erstgespräch & Bewertung', description: 'Wir besprechen Ihre Ziele und ich bewerte Ihre Immobilie.' },
  { num: '02', title: 'Vermarktungsstrategie', description: 'Gemeinsam entwickeln wir die optimale Verkaufsstrategie.' },
  { num: '03', title: 'Professionelles Exposé', description: 'Ich erstelle hochwertige Fotos und ein überzeugendes Exposé.' },
  { num: '04', title: 'Käuferqualifizierung', description: 'Nur seriöse, geprüfte Interessenten erhalten Besichtigungstermine.' },
  { num: '05', title: 'Verhandlung & Abschluss', description: 'Ich führe die Verhandlungen und begleite Sie bis zum Notar.' }
];

const benefits = [
  'Diskrete Vermarktung auf Wunsch',
  'Professionelle Immobilienfotografie',
  'Qualifizierte Käuferauswahl',
  'Verhandlungsführung in Ihrem Interesse',
  'Begleitung bis zur Schlüsselübergabe',
  'Transparente Kommunikation'
];

export default function Verkauf() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    property_type: '',
    message: ''
  });
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!privacyAccepted) return;
    
    setLoading(true);
    setStatus(null);

    const hubspotResult = await submitLead('verkauf', {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      propertyType: formData.property_type,
      message: formData.message
    });

    try {
      await axios.post(`${API}/api/lead`, { ...formData, service_type: 'Verkauf' });
    } catch (error) {
      console.error('Backend submission error:', error);
    }

    setLoading(false);
    
    if (hubspotResult.success) {
      setStatus('success');
      setFormData({ name: '', email: '', phone: '', property_type: '', message: '' });
      setPrivacyAccepted(false);
    } else {
      setStatus('error');
    }
  };

  const StatusMessage = () => (
    <AnimatePresence>
      {status && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className={`p-4 mb-6 flex items-start gap-3 ${
            status === 'success' 
              ? 'bg-green-500/10 border border-green-500/30' 
              : 'bg-red-500/10 border border-red-500/30'
          }`}
        >
          {status === 'success' ? (
            <>
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-green-400 font-medium">Vielen Dank!</p>
                <p className="text-green-400/80 text-sm">Serdar Dogan meldet sich in Kürze bei Ihnen.</p>
              </div>
            </>
          ) : (
            <>
              <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-400 font-medium">Es ist ein Fehler aufgetreten.</p>
                <p className="text-red-400/80 text-sm">
                  Bitte rufen Sie uns direkt an: 
                  <a href="tel:+491756900374" className="ml-1 inline-flex items-center gap-1 text-[#d1af6a] hover:underline">
                    <Phone className="w-3 h-3" /> +49 175 6900374
                  </a>
                </p>
              </div>
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <div data-testid="verkauf-page">
      <section className="relative pt-32 pb-20 bg-[#0a1120]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm mb-8">
            <Link to="/" className="text-[#c8c6c4] hover:text-[#d1af6a]">Home</Link>
            <ChevronRight className="w-4 h-4 text-[#c8c6c4]" />
            <Link to="/leistungen" className="text-[#c8c6c4] hover:text-[#d1af6a]">Leistungen</Link>
            <ChevronRight className="w-4 h-4 text-[#c8c6c4]" />
            <span className="text-[#d1af6a]">Verkauf</span>
          </nav>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <span className="font-accent text-[#d1af6a] text-xs tracking-[0.2em]">IMMOBILIENVERKAUF</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white mt-4 mb-6">Professioneller Immobilienverkauf</h1>
            <p className="text-[#c8c6c4] text-lg leading-relaxed">
              Verkaufen Sie Ihre Immobilie zum bestmöglichen Preis – diskret, professionell und mit persönlicher Betreuung von der Bewertung bis zur Schlüsselübergabe.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-display text-2xl sm:text-3xl text-white mb-6">Vorteile beim Verkauf mit mir</h2>
              <p className="text-[#c8c6c4] leading-relaxed mb-8">
                Der Verkauf einer Immobilie ist eine wichtige Entscheidung. Mit meiner Erfahrung und meinem Netzwerk im Rhein-Main-Gebiet sorge ich dafür, dass Sie den optimalen Preis erzielen – bei maximaler Diskretion.
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {benefits.map((benefit) => (
                  <li key={benefit} className="flex items-center gap-3 text-white">
                    <Check className="w-5 h-5 text-[#d1af6a] flex-shrink-0" />
                    <span className="text-sm">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="aspect-[4/3] bg-[#1a2640] border border-[#d1af6a]/20" />
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#1a2640]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="font-accent text-[#d1af6a] text-xs tracking-[0.2em]">DER VERKAUFSPROZESS</span>
            <h2 className="font-display text-3xl sm:text-4xl text-white mt-4">So läuft der Verkauf ab</h2>
          </div>
          <div className="space-y-8">
            {steps.map((step, index) => (
              <motion.div key={step.num} initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: index * 0.1 }} className="flex gap-6 items-start">
                <span className="font-display text-3xl text-[#d1af6a] flex-shrink-0 w-12">{step.num}</span>
                <div className="border-l border-[#d1af6a]/30 pl-6 pb-8">
                  <h3 className="font-subheading text-lg text-white mb-2">{step.title}</h3>
                  <p className="text-[#c8c6c4] text-sm">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#0a1120]">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="card-luxury p-8 md:p-12">
            <h2 className="font-display text-2xl sm:text-3xl text-white mb-2 text-center">Ich möchte meine Immobilie verkaufen</h2>
            <p className="text-[#c8c6c4] text-center mb-8">Kontaktieren Sie mich für ein unverbindliches Erstgespräch.</p>
            
            <StatusMessage />
            
            {status !== 'success' && (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="verkauf-form">
                <input type="text" placeholder="Ihr Name *" required value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} className="input-luxury w-full" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input type="email" placeholder="E-Mail *" required value={formData.email} onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))} className="input-luxury w-full" />
                  <input type="tel" placeholder="Telefon *" required value={formData.phone} onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))} className="input-luxury w-full" />
                </div>
                <Select value={formData.property_type} onValueChange={(value) => setFormData(prev => ({ ...prev, property_type: value }))}>
                  <SelectTrigger className="input-luxury w-full h-auto"><SelectValue placeholder="Art der Immobilie" /></SelectTrigger>
                  <SelectContent className="bg-[#1a2640] border-[#d1af6a]/20">
                    <SelectItem value="Wohnung">Wohnung</SelectItem>
                    <SelectItem value="Haus">Haus</SelectItem>
                    <SelectItem value="Grundstück">Grundstück</SelectItem>
                    <SelectItem value="Gewerbe">Gewerbe</SelectItem>
                  </SelectContent>
                </Select>
                <textarea placeholder="Ihre Nachricht (optional)" rows={3} value={formData.message} onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))} className="input-luxury w-full resize-none" />
                <PrivacyCheckbox checked={privacyAccepted} onCheckedChange={setPrivacyAccepted} />
                <button type="submit" disabled={loading || !privacyAccepted} className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed">
                  {loading ? 'Wird gesendet…' : 'Anfrage senden'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
